// src/Studio/canvas/utils/webglLut.ts
// WebGL2 3D-texture LUT renderer for the video frame pump.
// Replaces the CPU getImageData→trilinear→putImageData path with a single GPU draw call.

import type { LUT3D } from "../../types";

const VERT_SRC = `#version 300 es
in vec2 a_position;
in vec2 a_texCoord;
out vec2 v_texCoord;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texCoord = a_texCoord;
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
precision highp sampler3D;
in vec2 v_texCoord;
uniform sampler2D u_source;
uniform sampler3D u_lut;
uniform float u_intensity;
uniform float u_lutSize;
out vec4 outColor;
void main() {
  vec4 base = texture(u_source, v_texCoord);
  // Offset by half-texel to sample LUT cell centers, not edges
  float scale = (u_lutSize - 1.0) / u_lutSize;
  float offset = 0.5 / u_lutSize;
  vec3 lutCoord = base.rgb * scale + offset;
  vec3 lutColor = texture(u_lut, lutCoord).rgb;
  vec3 blended = mix(base.rgb, lutColor, u_intensity);
  outColor = vec4(blended, base.a);
}`;

export class WebGlLutRenderer {
  private canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private sourceTexture: WebGLTexture | null = null;
  private lutTexture: WebGLTexture | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private currentLutId: string | null = null;
  private uSourceLoc: WebGLUniformLocation | null = null;
  private uLutLoc: WebGLUniformLocation | null = null;
  private uIntensityLoc: WebGLUniformLocation | null = null;
  private uLutSizeLoc: WebGLUniformLocation | null = null;

  constructor() {
    this.canvas = document.createElement("canvas");
  }

  private init(): WebGL2RenderingContext | null {
    if (this.gl) return this.gl;
    let gl: WebGL2RenderingContext | null = null;
    try {
      gl = this.canvas.getContext("webgl2", {
        premultipliedAlpha: false,
        alpha: true,
        antialias: false,
        preserveDrawingBuffer: true,
      });
    } catch {
      return null;
    }
    if (!gl) return null;
    this.gl = gl;

    // Compile shaders
    const vs = gl.createShader(gl.VERTEX_SHADER);
    if (!vs) { this.dispose(); return null; }
    gl.shaderSource(vs, VERT_SRC);
    gl.compileShader(vs);
    if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) { gl.deleteShader(vs); this.dispose(); return null; }

    const fs = gl.createShader(gl.FRAGMENT_SHADER);
    if (!fs) { gl.deleteShader(vs); this.dispose(); return null; }
    gl.shaderSource(fs, FRAG_SRC);
    gl.compileShader(fs);
    if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) { gl.deleteShader(vs); gl.deleteShader(fs); this.dispose(); return null; }

    const program = gl.createProgram();
    if (!program) { gl.deleteShader(vs); gl.deleteShader(fs); this.dispose(); return null; }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) { gl.deleteProgram(program); this.dispose(); return null; }
    this.program = program;

    // Cache uniform locations
    this.uSourceLoc = gl.getUniformLocation(program, "u_source");
    this.uLutLoc = gl.getUniformLocation(program, "u_lut");
    this.uIntensityLoc = gl.getUniformLocation(program, "u_intensity");
    this.uLutSizeLoc = gl.getUniformLocation(program, "u_lutSize");

    // Fullscreen quad VAO
    const vao = gl.createVertexArray();
    if (!vao) { this.dispose(); return null; }
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer();
    if (!buf) { gl.deleteVertexArray(vao); this.dispose(); return null; }
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1, 0, 0,
       1, -1, 1, 0,
      -1,  1, 0, 1,
       1,  1, 1, 1,
    ]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, "a_position");
    const aTex = gl.getAttribLocation(program, "a_texCoord");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(aTex);
    gl.vertexAttribPointer(aTex, 2, gl.FLOAT, false, 16, 8);
    gl.bindVertexArray(null);
    this.vao = vao;

    // Source texture (2D — receives video/canvas frame each render)
    this.sourceTexture = gl.createTexture();
    if (this.sourceTexture) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    }

    return gl;
  }

  uploadLut(lut: LUT3D): void {
    const gl = this.init();
    if (!gl || !this.program) return;
    const lutId = `${lut.title}_${lut.size}_${lut.data.length}`;
    if (lutId === this.currentLutId && this.lutTexture) return;

    if (!this.lutTexture) {
      this.lutTexture = gl.createTexture();
      if (!this.lutTexture) return;
    }
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_3D, this.lutTexture);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // Convert Float32 RGB triplets to RGBA Uint8 for texImage3D
    const size = lut.size;
    const count = size * size * size;
    const rgba = new Uint8Array(count * 4);
    for (let i = 0; i < count; i++) {
      rgba[i * 4] = Math.round(Math.min(1, Math.max(0, lut.data[i * 3])) * 255);
      rgba[i * 4 + 1] = Math.round(Math.min(1, Math.max(0, lut.data[i * 3 + 1])) * 255);
      rgba[i * 4 + 2] = Math.round(Math.min(1, Math.max(0, lut.data[i * 3 + 2])) * 255);
      rgba[i * 4 + 3] = 255;
    }
    gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGBA8, size, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, rgba);
    this.currentLutId = lutId;
  }

  /** Render source canvas through the LUT. Returns the output canvas (same size as input). */
  render(source: HTMLCanvasElement, intensity: number, lutSize: number): HTMLCanvasElement | null {
    const gl = this.init();
    if (!gl || !this.program || !this.lutTexture || !this.sourceTexture || !this.vao) return null;

    const w = source.width;
    const h = source.height;
    if (w <= 0 || h <= 0) return null;
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    gl.viewport(0, 0, w, h);

    // Upload source frame
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

    // Bind LUT 3D texture
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_3D, this.lutTexture);

    // Set uniforms and draw
    gl.useProgram(this.program);
    gl.uniform1i(this.uSourceLoc, 0);
    gl.uniform1i(this.uLutLoc, 1);
    gl.uniform1f(this.uIntensityLoc, Math.max(0, Math.min(1, intensity / 100)));
    gl.uniform1f(this.uLutSizeLoc, lutSize);

    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);

    return this.canvas;
  }

  dispose(): void {
    const gl = this.gl;
    if (!gl) {
      this.gl = null;
      this.program = null;
      this.sourceTexture = null;
      this.lutTexture = null;
      this.vao = null;
      this.currentLutId = null;
      return;
    }
    if (this.program) { try { gl.deleteProgram(this.program); } catch {} }
    if (this.sourceTexture) { try { gl.deleteTexture(this.sourceTexture); } catch {} }
    if (this.lutTexture) { try { gl.deleteTexture(this.lutTexture); } catch {} }
    if (this.vao) { try { gl.deleteVertexArray(this.vao); } catch {} }
    this.gl = null;
    this.program = null;
    this.sourceTexture = null;
    this.lutTexture = null;
    this.vao = null;
    this.currentLutId = null;
    this.uSourceLoc = null;
    this.uLutLoc = null;
    this.uIntensityLoc = null;
    this.uLutSizeLoc = null;
  }
}
